/**
 * The shopping cart.
 *
 * Backed by localStorage AND, for a signed-in customer, by the account itself
 * (see store/cart-sync.js). localStorage remains the immediate source of truth
 * — the cart has to work instantly and offline — while the server copy is what
 * makes it survive a cleared browser and follow the customer to another
 * machine.
 *
 * Every mutation stamps `updatedAt`. That timestamp is the whole conflict
 * resolution story: when two devices both hold a cart, the newer one wins.
 * Without it the sync would have no way to tell a stale cart from a fresh one.
 */

const CART_KEY = "cart";
const UPDATED_AT_KEY = "cart_updated_at";

/** Reads the persisted cart, tolerating the `null` string older builds wrote. */
function readStoredItems() {
  try {
    const raw = JSON.parse(localStorage.getItem(CART_KEY));
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
}

function readStoredUpdatedAt() {
  const raw = localStorage.getItem(UPDATED_AT_KEY);
  return raw && !Number.isNaN(Date.parse(raw)) ? raw : null;
}

/**
 * Persists the cart and stamps it.
 *
 * Called from every mutation rather than from the actions, so a commit from
 * anywhere — including devtools — cannot leave the two out of step.
 */
function persist(state, { touch = true } = {}) {
  localStorage.setItem(CART_KEY, JSON.stringify(state.items));
  if (touch) {
    state.updatedAt = new Date().toISOString();
    localStorage.setItem(UPDATED_AT_KEY, state.updatedAt);
  }
}

const initialState = {
  items: readStoredItems(),
  /** ISO-8601 UTC. Null when this browser has never held a cart. */
  updatedAt: readStoredUpdatedAt(),
};

export const cart = {
  namespaced: true,
  state: initialState,
  actions: {
    addSubscriptionToCart({ commit }, subscription) {
      commit("addToCart", subscription);
    },
    addToCart({ commit }, payload = null) {
      commit("addToCart", payload);
    },
    loadCart({ commit }, payload = null) {
      commit("loadCart", payload);
    },
    removeFromCart({ commit }, payload = null) {
      commit("removeFromCart", payload);
    },
    clearCart({ commit }, payload = null) {
      commit("clearCart");
    },
    saveCart({ commit }, payload = null) {
      const { item, index } = payload;
      commit("saveCart", { item, index });
    },

    /**
     * Replaces the cart with the server's copy.
     *
     * Does NOT re-stamp `updatedAt` — the server's timestamp is carried over
     * instead. Stamping here would make a cart that was merely *downloaded*
     * look newer than the one it came from, and the next sync would push it
     * straight back over a genuinely newer edit made elsewhere.
     */
    adoptRemoteCart({ commit }, { items, updatedAt }) {
      commit("adoptRemoteCart", { items, updatedAt });
    },

    /**
     * Drops the cart without touching the server.
     *
     * Used on logout: the next person to use this browser must not inherit the
     * previous account's cart, and pushing an empty cart to the server would
     * delete a cart the customer still wants.
     */
    forgetLocalCart({ commit }) {
      commit("forgetLocalCart");
    },
  },
  mutations: {
    saveCart(state, { item, index }) {
      state.items.splice(index, 1);
      state.items.push(item);
      persist(state);
    },
    addToCart(state, item) {
      // Add ID if not present
      if (!item.id) {
        item.id = Date.now();
      }

      state.items.push(item);
      persist(state);
    },
    removeFromCart(state, index) {
      state.items.splice(index, 1);
      persist(state);
    },
    loadCart(state) {
      const stored = readStoredItems();
      if (stored.length > 0) {
        state.items = stored;
      }
      state.updatedAt = readStoredUpdatedAt();
    },
    clearCart(state) {
      state.items.splice(0, state.items.length);
      persist(state);
    },
    adoptRemoteCart(state, { items, updatedAt }) {
      state.items.splice(0, state.items.length, ...(Array.isArray(items) ? items : []));
      localStorage.setItem(CART_KEY, JSON.stringify(state.items));

      state.updatedAt = updatedAt ?? null;
      if (state.updatedAt) {
        localStorage.setItem(UPDATED_AT_KEY, state.updatedAt);
      } else {
        localStorage.removeItem(UPDATED_AT_KEY);
      }
    },
    forgetLocalCart(state) {
      state.items.splice(0, state.items.length);
      state.updatedAt = null;
      localStorage.removeItem(CART_KEY);
      localStorage.removeItem(UPDATED_AT_KEY);
    },
  },
};
